import { z } from 'zod';
import { isoDateNullableSchema, isoDateSchema } from '../dates';
import { marginPctSchema } from '../rates';

/** Empty strings become null for optional text fields coming from forms. */
export const optionalText = z
  .union([z.string(), z.null()])
  .optional()
  .transform((v) => (v === undefined ? undefined : v === null || v.trim() === '' ? null : v));

export const requiredText = z.string().trim().min(1);

/** Like `optionalText`, but a non-empty value must be an e-mail address. */
export const optionalEmail = optionalText.pipe(
  z.union([z.string().trim().email({ message: 'Invalid e-mail address' }), z.null()]).optional(),
);

export const optionalNumber = z
  .union([z.number(), z.string(), z.null()])
  .optional()
  .transform((v, ctx) => {
    if (v === undefined) return undefined;
    if (v === null || v === '') return null;
    const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
    if (!Number.isFinite(n)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Expected a number' });
      return z.NEVER;
    }
    return n;
  });

/** margin_pct override: number sets, null clears ("use global"). */
export const marginOverrideSchema = z
  .union([marginPctSchema, z.null(), z.literal('')])
  .optional()
  .transform((v) => (v === '' ? null : v));

export const stringArray = z.array(z.string().trim().min(1)).default([]);

export const optionalDate = isoDateNullableSchema.optional();
export const requiredDate = isoDateSchema;

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type Pagination = z.infer<typeof paginationSchema>;

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const uuidSchema = z.string().uuid();

/** Any human form accepted for entity lookup: UUID, DC-C-…, DC-J-…, request number, external ref. */
export const refSchema = z.string().trim().min(1).max(200);

export const languageEntrySchema = z.object({
  language: z.string().trim().min(1),
  level: z.string().trim().default('not stated'),
});
export type LanguageEntry = z.infer<typeof languageEntrySchema>;

export const listQueryBase = z.object({
  text: z.string().trim().optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(200).optional(),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).optional(),
});
